'use client';

import { c as _c } from "react/compiler-runtime";
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import Image from 'next/image'; // ALLOW-RAW-IMG: decorative static slides — IntroWidget FR-015/016 tests require data-testid + layout classes on the img node itself
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { AppUpsellSlide } from '@/components/app-upsells/app-upsell-slide';
import { AnimatedButton } from '@/components/ui/animated-button';
import { CarouselDots } from '@/components/ui/carousel-dots';
import { Cancel01Icon } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { onEvent } from '@/events';
import { useHydrated } from '@/hooks/ui/use-hydrated';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { appUpsellProperties } from '@/lib/app-upsells/analytics';
import { DURATION_POSITION_SMALL_MS, MOTION_ENTRANCE } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import { useIntroWidgetDismissalStore } from '@/stores/intro-widget-dismissal';
import { INTRO_SLIDES, type IntroSlide as IntroSlideShape } from './intro-widget-slides';

// ---------------------------------------------------------------------------
// Module-scope constants (React Compiler friendly — no useMemo)
// ---------------------------------------------------------------------------

const AUTOPLAY_DELAY_MS: number = 5000;

/**
 * Embla's `duration` is in animation-tick units (≈ ms / 10 at 60 fps), not
 * milliseconds. Convert from `DURATION_POSITION_SMALL_MS` so the slide-scroll
 * matches every other position-class motion in the UI.
 */
const EMBLA_SLIDE_DURATION: number = DURATION_POSITION_SMALL_MS / 10;

// Tailwind `animate-in` keyframes (from tailwindcss-animate). These fire once
// on element mount, so a fresh `key` per slide-activation re-triggers them.
// CSS animations are simpler + more reliable than Motion props inside an
// embla viewport because they don't race with embla's slide-scroll transform.
// Entrance animation matches the dot orientation per breakpoint:
// sm + md show vertical dots on the right → title slides up from below.
// lg+ shows horizontal dots bottom-left → title slides in from the left
// (we reset --tw-enter-translate-y so the bottom-6 offset doesn't compose
// into a diagonal slide).
const TITLE_ENTRANCE_CLASS = 'animate-in fade-in-0 slide-in-from-bottom-6 lg:[--tw-enter-translate-y:0px] lg:slide-in-from-left-6 duration-slow ease-default fill-mode-both motion-reduce:animate-none' as const;
const IMAGE_ENTRANCE_CLASS = 'animate-in fade-in-0 duration-fast delay-75 ease-default fill-mode-both motion-reduce:animate-none' as const;

// ---------------------------------------------------------------------------
// IntroWidget — decorative, auto-cycling promotional surface above the market
// feed. Two-column at md+, single-column on mobile.
// ---------------------------------------------------------------------------

export function IntroWidget(t0) {
  const $ = _c(27);
  const {
    active: t1
  } = t0;
  const active = t1 === undefined ? true : t1;
  const reduceMotion = useReducedMotion();
  const isLgUp = useMediaQuery("(min-width: 1024px)");
  const t = useTranslations("markets.intro_widget");
  const tApp = useTranslations("app_upsells");
  const showApp = useFeatureGate("app_upsells") === true;
  const slideCount = INTRO_SLIDES.length;
  const dismissed = useIntroWidgetDismissalStore(_temp);
  const dismiss = useIntroWidgetDismissalStore(_temp2);
  const mounted = useHydrated();
  const [autoplayPlugin] = useState(_temp3);
  let t2;
  if ($[0] !== autoplayPlugin || $[1] !== reduceMotion || $[2] !== showApp) {
    t2 = showApp || reduceMotion === true ? [] : [autoplayPlugin];
    $[0] = autoplayPlugin;
    $[1] = reduceMotion;
    $[2] = showApp;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const plugins = t2;
  const emblaDuration = reduceMotion === true ? 0 : isLgUp ? EMBLA_SLIDE_DURATION : 0;
  const t3 = !showApp;
  let t4;
  if ($[4] !== emblaDuration || $[5] !== t3) {
    t4 = {
      active: t3,
      loop: true,
      align: "start",
      duration: emblaDuration
    };
    $[4] = emblaDuration;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t4, plugins);
  let t5;
  let t6;
  if ($[7] !== emblaApi || $[8] !== emblaDuration || $[9] !== showApp) {
    t5 = () => {
      if (emblaApi === undefined) {
        return;
      }
      emblaApi.reInit({
        active: !showApp,
        loop: true,
        align: "start",
        duration: emblaDuration
      });
    };
    t6 = [emblaApi, emblaDuration, showApp];
    $[7] = emblaApi;
    $[8] = emblaDuration;
    $[9] = showApp;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  const [selectedIndex, setSelectedIndex] = useState(0);
  let t7;
  let t8;
  if ($[12] !== emblaApi) {
    t7 = () => {
      if (emblaApi === undefined) {
        return;
      }
      const syncSelected = function syncSelected() {
        if (emblaApi === undefined) {
          return;
        }
        setSelectedIndex(emblaApi.selectedScrollSnap());
      };
      emblaApi.on("select", syncSelected);
      syncSelected();
      return () => {
        emblaApi.off("select", syncSelected);
      };
    };
    t8 = [emblaApi];
    $[12] = emblaApi;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  if (mounted && dismissed) {
    return null;
  }
  let t9;
  if ($[15] !== active || $[16] !== dismiss || $[17] !== emblaApi || $[18] !== emblaRef || $[19] !== reduceMotion || $[20] !== selectedIndex || $[21] !== showApp || $[22] !== t || $[23] !== tApp) {
    t9 = active && <m.div key="intro-widget" className="hidden md:block md:col-span-2 md:row-start-1 md:h-24 lg:col-start-2 lg:col-span-2 lg:h-full" initial={reduceMotion === true ? false : {
      opacity: 0,
      scale: 0.98
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={reduceMotion === true ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.98
    }} transition={reduceMotion === true ? {
      duration: 0
    } : MOTION_ENTRANCE}><Panel surface="solid" elevation="flat" className="hidden md:flex md:col-span-2 md:row-start-1 md:h-24 overflow-hidden p-0 lg:col-start-2 lg:col-span-2 lg:h-full gap-0" data-testid="intro-widget-panel"><div className="relative flex-1 overflow-hidden rounded-card" data-testid="intro-widget-inner">{showApp ? <AppUpsellSlide /> : <><div aria-hidden="true" className="chart-surface-dots pointer-events-none absolute inset-0 opacity-40 blur-[0.5px]" /><div ref={emblaRef} className="relative h-full overflow-hidden" data-pager-ignore={true}><div className="flex h-full">{INTRO_SLIDES.map((slide, idx) => <div key={slide.id} className="relative h-full min-w-0 flex-[0_0_100%]" data-testid="intro-widget-slide" data-slide-id={slide.id}><IntroSlideContent slide={slide} isActive={idx === selectedIndex} /></div>)}</div></div><CarouselDots count={slideCount} onSelect={index => emblaApi?.scrollTo(index)} itemAriaLabel={(index_0, total) => tApp("slide_label", {
              index: index_0 + 1,
              total
            })} activeIndex={selectedIndex} orientation="vertical" progressDurationMs={AUTOPLAY_DELAY_MS} activeLength="0.875rem" inactiveLength="0.25rem" thickness="0.25rem" className="absolute right-3 top-1/2 -translate-y-1/2 lg:hidden" testId="intro-widget-dots-mobile" dotTestId={_temp4} /><CarouselDots count={slideCount} onSelect={index_1 => emblaApi?.scrollTo(index_1)} itemAriaLabel={(index_2, total_0) => tApp("slide_label", {
              index: index_2 + 1,
              total: total_0
            })} activeIndex={selectedIndex} orientation="horizontal" progressDurationMs={AUTOPLAY_DELAY_MS} className="absolute bottom-3 left-8 hidden lg:flex" testId="intro-widget-dots" dotTestId={_temp5} /></>}<AnimatedButton variant="tertiary" size="xs" prefix={Cancel01Icon} onClick={() => {
            if (showApp) {
              onEvent("app_upsell_dismissed", appUpsellProperties("feed", "feed"));
            }
            dismiss();
          }} aria-label={t("dismiss")} disableHoverScale={true} data-testid="intro-widget-dismiss" className="absolute right-2 top-2 opacity-60 hover:opacity-100" /></div></Panel></m.div>;
    $[15] = active;
    $[16] = dismiss;
    $[17] = emblaApi;
    $[18] = emblaRef;
    $[19] = reduceMotion;
    $[20] = selectedIndex;
    $[21] = showApp;
    $[22] = t;
    $[23] = tApp;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t9) {
    t10 = <LazyMotion features={domAnimation}><AnimatePresence initial={false}>{t9}</AnimatePresence></LazyMotion>;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}

// ---------------------------------------------------------------------------
// IntroSlideContent — single slide. Title slides in from left; image fades in
// slightly faster and 75 ms later so the eye lands on the headline first.
// `key={isActive ? 'on' : 'off'}` forces a remount on each activation, which
// re-triggers the CSS keyframe entrance animation. `motion-reduce:animate-none`
// short-circuits both animations under prefers-reduced-motion.
// ---------------------------------------------------------------------------
function _temp5() {
  return "intro-widget-dot";
}
function _temp4() {
  return "intro-widget-dot-mobile";
}
function _temp3() {
  return Autoplay({
    delay: AUTOPLAY_DELAY_MS,
    stopOnInteraction: false,
    stopOnMouseEnter: true
  });
}
function _temp2(s_0) {
  return s_0.dismiss;
}
function _temp(s) {
  return s.dismissed;
}
function IntroSlideContent(t0) {
  const $ = _c(19);
  const {
    slide,
    isActive
  } = t0;
  const t = useTranslations("markets.intro_widget");
  const t1 = `${slide.id}-${isActive ? "on" : "off"}`;
  const t2 = isActive && TITLE_ENTRANCE_CLASS;
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex-1 text-balance pl-5 pr-10 text-left text-heading-lg font-bold text-foreground-primary md:pl-8 lg:pr-4 lg:pt-6 lg:text-display-lg", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `titles.${slide.id}`;
  let t5;
  if ($[2] !== t || $[3] !== t4) {
    t5 = t(t4);
    $[2] = t;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t1 || $[6] !== t3 || $[7] !== t5) {
    t6 = <h2 key={t1} className={t3} data-testid="intro-widget-title">{t5}</h2>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = `${slide.id}-img-${isActive ? "on" : "off"}`;
  const t8 = slide.imageSrc;
  const t9 = isActive && IMAGE_ENTRANCE_CLASS;
  let t10;
  if ($[9] !== t9) {
    t10 = cn("ml-auto hidden h-full w-auto shrink-0 pl-0 object-right lg:block", t9);
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = slide.id === "t1";
  let t12;
  if ($[11] !== slide.imageSrc || $[12] !== t10 || $[13] !== t11 || $[14] !== t7) {
    t12 = <Image key={t7} src={t8} alt="" aria-hidden="true" data-testid="intro-widget-image" width={309} height={252} className={t10} priority={t11} />;
    $[11] = slide.imageSrc;
    $[12] = t10;
    $[13] = t11;
    $[14] = t7;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12 || $[17] !== t6) {
    t13 = <div className="flex h-full items-center lg:items-stretch">{t6}{t12}</div>;
    $[16] = t12;
    $[17] = t6;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  return t13;
}